import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentUserOrganizationId } from './referenceDataCache';
import type { Client } from '../types';

/**
 * Gerarchia aziende (madre/controllate) — modello Contatti/Aziende first-class.
 * Riusa `client_relationships` con relationship_type='azienda_madre':
 *   source_client_id = controllata (child), target_client_id = madre (parent).
 * Un'azienda ha UNA madre (imposta via setParent che sostituisce la precedente).
 * Vedi MD/architecture/contatti-aziende-firstclass-migrazione.md
 */

const TYPE = 'azienda_madre';
const COMPANY_SELECT = 'id, business_name, entity_type, city, vat_number';

export interface HierarchyLink {
  id: string;
  company: Pick<Client, 'id' | 'business_name'> & Record<string, any>;
}

interface CompanyHierarchyStore {
  loading: boolean;
  /** L'azienda madre di companyId (o null) */
  fetchParent: (companyId: string) => Promise<HierarchyLink | null>;
  /** Le controllate di companyId */
  fetchSubsidiaries: (companyId: string) => Promise<HierarchyLink[]>;
  /** Imposta la madre (sostituisce l'eventuale precedente). Guardia anti-loop base. */
  setParent: (childId: string, parentId: string) => Promise<boolean>;
  /** Rimuove la madre (soft delete) */
  removeParent: (childId: string) => Promise<boolean>;
}

export const useCompanyHierarchyStore = create<CompanyHierarchyStore>((set) => ({
  loading: false,

  fetchParent: async (companyId) => {
    set({ loading: true });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('client_relationships')
        .select(`id, company:target_client_id (${COMPANY_SELECT})`)
        .eq('organization_id', orgId)
        .eq('relationship_type', TYPE)
        .eq('source_client_id', companyId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data || !(data as any).company) return null;
      return { id: (data as any).id, company: (data as any).company };
    } catch {
      return null;
    } finally {
      set({ loading: false });
    }
  },

  fetchSubsidiaries: async (companyId) => {
    set({ loading: true });
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('client_relationships')
        .select(`id, company:source_client_id (${COMPANY_SELECT})`)
        .eq('organization_id', orgId)
        .eq('relationship_type', TYPE)
        .eq('target_client_id', companyId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return ((data as any[]) || [])
        .filter((r) => r.company)
        .map((r) => ({ id: r.id, company: r.company }));
    } catch {
      return [];
    }
  },

  setParent: async (childId, parentId) => {
    if (childId === parentId) return false; // no self-parent
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return false;
      // Guardia anti-loop base: il parent non può avere childId come sua madre.
      const { data: reverse } = await supabase
        .from('client_relationships')
        .select('id')
        .eq('organization_id', orgId)
        .eq('relationship_type', TYPE)
        .eq('source_client_id', parentId)
        .eq('target_client_id', childId)
        .eq('is_active', true)
        .limit(1);
      if (reverse && reverse.length > 0) return false;

      // Disattiva l'eventuale madre precedente (un solo parent attivo)
      await supabase
        .from('client_relationships')
        .update({ is_active: false })
        .eq('organization_id', orgId)
        .eq('relationship_type', TYPE)
        .eq('source_client_id', childId)
        .eq('is_active', true);

      // upsert: riattiva se il link (child,parent) esisteva già inattivo
      const { error } = await supabase
        .from('client_relationships')
        .upsert({
          source_client_id: childId,
          target_client_id: parentId,
          relationship_type: TYPE,
          organization_id: orgId,
          is_active: true,
        }, { onConflict: 'source_client_id,target_client_id,relationship_type' });
      if (error) throw error;
      return true;
    } catch {
      return false;
    }
  },

  removeParent: async (childId) => {
    try {
      const orgId = await getCurrentUserOrganizationId();
      if (!orgId) return false;
      const { error } = await supabase
        .from('client_relationships')
        .update({ is_active: false })
        .eq('organization_id', orgId)
        .eq('relationship_type', TYPE)
        .eq('source_client_id', childId)
        .eq('is_active', true);
      if (error) throw error;
      return true;
    } catch {
      return false;
    }
  },
}));
